import { useEffect, useState } from "react";
import { groupRunAt, type Draft } from "@heraion/shared";

import type { V3Navigation } from "./useV3Navigation";

// The day's multiple selection (#336, brief §7.2 «Selezione multipla»): on
// with «Select» in the day's head, off with «Done». It lives on the day being
// shown, in the «Exercises» lens: another day or the other lens ends it. A
// tap on a superset member takes the whole group (groupRunAt), so the bar's
// actions never split one. Kept by id, so a move keeps it.
export function useDaySelection(draft: Draft, nav: V3Navigation) {
  const [ids, setIds] = useState<ReadonlySet<string> | null>(null);
  const slots = draft.sessions[nav.dayIdx]?.slots ?? [];

  useEffect(() => {
    setIds(null);
  }, [nav.dayIdx, nav.lens]);

  const active = ids !== null && nav.lens === "exercises" && nav.openSlotIdx === null;
  /** The selected exercises still in the day, in day order. */
  const selected = active ? slots.filter((s) => ids.has(s.id)).map((s) => s.id) : [];

  const toggle = (slotIdx: number) => {
    const slot = slots[slotIdx];
    if (!slot) return;
    const [a, b] = groupRunAt(slots, slotIdx);
    const run = slots.slice(a, b + 1).map((s) => s.id);
    setIds((cur) => {
      if (cur === null) return cur;
      const on = cur.has(slot.id);
      const next = new Set(cur);
      for (const id of run) {
        if (on) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  };

  return {
    active,
    selected,
    isSelected: (id: string) => active && ids.has(id),
    start: () => setIds(new Set()),
    stop: () => setIds(null),
    toggle,
    clear: () => setIds((cur) => (cur === null ? cur : new Set())),
  };
}

export type DaySelection = ReturnType<typeof useDaySelection>;
